import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import BreadcrumbsSchema from "../components/BreadcrumbsSchema";
import GroupSessionDetailsView from "../components/ui/GroupSessionDetailsView";
import AuthRequiredModal from "../components/ui/AuthRequiredModal";
import { sessionsApi } from "../api/sessionsApi";
import { appointmentsApi } from "../api/appointmentsApi";
import { useAuth } from "../auth/authStore";
import { useToast } from "../ui/toast/ToastProvider";
import {
  isBookingTemporarilyDisabled,
  triggerMaintenanceBanner,
} from "../utils/maintenanceBanner";

import { resolveUrl } from "../utils/urls";
import { assignExternalUrl, openExternalUrl } from "../utils/safeUrl";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function fmtDateTimeRange(startIso, endIso) {
  try {
    const s = startIso ? new Date(startIso) : null;
    const e = endIso ? new Date(endIso) : null;
    if (!s || Number.isNaN(s.getTime())) return "—";
    const dd = `${pad2(s.getDate())}.${pad2(s.getMonth() + 1)}.${s.getFullYear()}`;
    const st = `${pad2(s.getHours())}:${pad2(s.getMinutes())}`;
    let et = "";
    if (e && !Number.isNaN(e.getTime())) et = `${pad2(e.getHours())}:${pad2(e.getMinutes())}`;
    return et ? `${dd} • ${st}–${et}` : `${dd} • ${st}`;
  } catch {
    return "—";
  }
}

function joinTitles(arr) {
  return (arr || [])
    .map((x) => {
      if (!x) return null;
      if (typeof x === "string") return x;
      return x?.title || x?.name || null;
    })
    .filter(Boolean)
    .join(", ");
}

function diffMinutes(startIso, endIso) {
  try {
    const s = new Date(startIso);
    const e = new Date(endIso);
    const ms = e.getTime() - s.getTime();
    if (!Number.isFinite(ms) || ms <= 0) return null;
    return Math.round(ms / 60000);
  } catch {
    return null;
  }
}

function safeText(v) {
  return String(v || "").replace(/\s+/g, " ").trim();
}

function takeSnippet(text, max = 180) {
  const s = safeText(text);
  if (!s) return "";
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function siteOrigin() {
  const env = (typeof process !== "undefined" && process?.env) ? process.env : {};
  const v = String(env.REACT_APP_SITE_URL || "").trim();
  if (v) {
    try {
      return new URL(v).toString().replace(/\/$/, "");
    } catch {
      // ignore
    }
  }
  if (typeof window === "undefined") return "";
  return window.location.origin;
}

function absUrl(path) {
  const origin = siteOrigin();
  if (!origin) return "";
  try {
    return new URL(path, origin).toString();
  } catch {
    return "";
  }
}

const PAID_STATUSES = new Set(["PAID", "CONFIRMED", "SUCCEEDED"]);
function isPaidStatus(status) {
  return PAID_STATUSES.has(String(status || "").toUpperCase());
}

function pickGroupSessionIdFromAppointment(a) {
  return (
    a?.groupSessionId ??
    a?.sessionId ??
    a?.session_id ??
    a?.group_session_id ??
    a?.groupSession?.id ??
    a?.session?.id ??
    null
  );
}

function pickTelemostUrl(x) {
  const s = String(x?.telemostUrl || x?.telemost_url || x?.telemostLink || "").trim();
  return s || null;
}

function pickPaymentUrl(x) {
  const s = String(x?.paymentUrl || x?.payment_url || x?.confirmationUrl || x?.confirmation_url || "").trim();
  return s || null;
}

function normalizeGroupSession(raw) {
  const id = raw?.id ?? raw?.sessionId ?? null;

  const title = raw?.title || "Групповая сессия";
  const coverImg = raw?.coverImg || raw?.cover_img || null;

  const startDateTime = raw?.startDateTime || raw?.start_datetime || null;
  const endDateTime = raw?.endDateTime || raw?.end_datetime || null;

  const priceAtTime = raw?.priceAtTime ?? raw?.price_at_time ?? null;
  const capacityClients = raw?.capacityClients ?? raw?.capacity_clients ?? null;
  const minCapacity = raw?.minCapacity ?? raw?.min_capacity ?? null;

  const participantsCount =
    raw?.participantsCount ??
    raw?.clientsCount ??
    raw?.membersCount ??
    (Array.isArray(raw?.participants) ? raw.participants.length : 0);

  const psychologist = raw?.psychologist || raw?.owner || raw?.author || null;
  const psyName = psychologist?.name || raw?.psychologistName || raw?.psychologist_name || "Психолог";

  const psyAvatarUrl =
    psychologist?.avatarUrl ||
    psychologist?.avatar_url ||
    psychologist?.photoUrl ||
    psychologist?.photo_url ||
    raw?.psychologistAvatarUrl ||
    raw?.psychologist_avatar_url ||
    null;

  const psyAbout =
    psychologist?.shortBio ||
    psychologist?.short_bio ||
    psychologist?.about ||
    psychologist?.description ||
    raw?.psychologistAbout ||
    raw?.psychologist_about ||
    "";

  const psyExperienceYears =
    psychologist?.experienceYears ??
    psychologist?.experience_years ??
    raw?.psychologistExperienceYears ??
    raw?.psychologist_experience_years ??
    null;

  const psyExperience =
    psychologist?.experience ||
    raw?.psychologistExperience ||
    raw?.psychologist_experience ||
    (psyExperienceYears != null ? `${psyExperienceYears}+ лет опыта работы` : "");

  const formatLabel =
    raw?.formatLabel ||
    raw?.format_label ||
    raw?.format ||
    raw?.sessionFormat ||
    raw?.session_format ||
    "Онлайн";

  const paidCount = raw?.paidCount ?? raw?.paid_count ?? null;

  const themes = raw?.themes?.length ? raw.themes : (raw?.themeLabels || []).map(t => typeof t === "string" ? { title: t } : t);
  const methods = raw?.methods?.length ? raw.methods : (raw?.methodLabels || []).map(m => typeof m === "string" ? { title: m } : m);
  const description = raw?.description || "";

  const telemostUrl = raw?.telemostUrl || raw?.telemost_url || raw?.telemostLink || null;
  const paymentUrl = raw?.paymentUrl || raw?.payment_url || raw?.confirmationUrl || null;
  const status = raw?.status || null;

  return {
    id,
    title,
    coverImg,
    startDateTime,
    endDateTime,
    priceAtTime,
    capacityClients,
    minCapacity,
    participantsCount,
    paidCount,
    psyName,
    psyAvatarUrl,
    psyAbout,
    psyExperience,
    psyExperienceYears,
    formatLabel,
    themes,
    methods,
    description,
    telemostUrl,
    paymentUrl,
    status,
    raw,
  };
}

function toModalSession(s, extra = {}) {
  if (!s) return null;
  const duration = s.startDateTime && s.endDateTime ? diffMinutes(s.startDateTime, s.endDateTime) : null;
  return {
    id: s.id,
    title: s.title || "Групповая сессия",
    name: s.psyName || "Психолог",
    image: s.coverImg ? resolveUrl(s.coverImg) : "",
    coverImg: s.coverImg || "",
    startDateTime: s.startDateTime || null,
    endDateTime: s.endDateTime || null,
    sessionDuration: duration || 60,
    pricePerSession: s.priceAtTime ?? null,
    priceAtTime: s.priceAtTime ?? null,
    date: fmtDateTimeRange(s.startDateTime, s.endDateTime),
    themes: s.themes?.length ? s.themes : [],
    method: s.methods?.length ? s.methods : [],
    themesText: s.themes?.length ? joinTitles(s.themes) : "",
    methodText: s.methods?.length ? joinTitles(s.methods) : "",
    description: s.description || "",
    capacityClients: s.capacityClients ?? null,
    minCapacity: s.minCapacity ?? null,
    participantsCount: s.participantsCount ?? 0,
    paidCount: s.paidCount ?? null,
    psyName: s.psyName || "",
    psyAvatarUrl: s.psyAvatarUrl || "",
    psyAbout: s.psyAbout || "",
    psyExperience: s.psyExperience || "",
    psyExperienceYears: s.psyExperienceYears ?? null,
    formatLabel: s.formatLabel || "Онлайн",
    raw: s.raw || null,
    paymentUrl: s.paymentUrl || null,
    telemostUrl: s.telemostUrl || null,
    status: s.status || null,
    ...extra,
  };
}

export default function GroupSessionPage() {
  const { id } = useParams();
  const auth = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const isAuthed =
    typeof auth?.isAuthenticated === "boolean"
      ? auth.isAuthenticated
      : Boolean(auth?.isAuthed ?? auth?.token ?? auth?.me);

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState("");

  const [normalized, setNormalized] = useState(null);
  const [session, setSession] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [authRequiredOpen, setAuthRequiredOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    setError("");
    setNormalized(null);
    setSession(null);

    (async () => {
      try {
        const raw = await sessionsApi.getPublicGroupSession(id);
        if (!alive) return;
        const n = normalizeGroupSession(raw);
        const sessionIdKey = String(n?.id ?? id);

        let extra = {};
        if (isAuthed) {
          const allAppointments = await appointmentsApi.listMyAll().catch(() => []);
          if (!alive) return;

          const matched = (allAppointments || []).find(
            (a) => String(pickGroupSessionIdFromAppointment(a) ?? "") === sessionIdKey
          );

          if (matched) {
            const status = String(matched?.status || matched?.bookingStatus || "").toUpperCase();
            const bookingId = matched?.id ?? matched?.bookingId ?? matched?.booking_id ?? null;
            const isCancelled = status === "CANCELLED" || status === "CANCELED";

            extra = {
              alreadyJoined: !isCancelled,
              joinedBookingId: bookingId != null ? String(bookingId) : null,
              status: status || null,
              telemostUrl: pickTelemostUrl(matched) || n.telemostUrl || null,
              paymentUrl: pickPaymentUrl(matched) || n.paymentUrl || null,
              isPaid: isPaidStatus(status),
            };
          }
        }

        setNormalized(n);
        setSession(toModalSession(n, extra));
      } catch (e) {
        if (!alive) return;
        const st = e?.status ?? e?.response?.status;
        if (st === 404) setNotFound(true);
        setError(e?.message || "Не удалось загрузить групповую сессию");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id, isAuthed]);

  const crumbs = useMemo(() => {
    const title = normalized?.title || "Групповая сессия";
    return [
      { label: "Главная", to: "/" },
      { label: "Сессии", to: "/sessions" },
      { label: title },
    ];
  }, [normalized?.title]);

  const canonicalUrl = useMemo(() => absUrl(`/group-sessions/${id}`), [id]);

  const siteName = (typeof process !== "undefined" && process?.env?.REACT_APP_SITE_NAME)
    ? process.env.REACT_APP_SITE_NAME
    : "Бюро Души";

  const metaTitle = useMemo(() => {
    const t = normalized?.title || "Групповая сессия";
    return `${t} — групповая сессия | ${siteName}`;
  }, [normalized?.title, siteName]);

  const metaDescription = useMemo(() => {
    if (!normalized) return "";
    const parts = [
      normalized.psyName ? `Психолог: ${safeText(normalized.psyName)}` : "",
      normalized.startDateTime ? `Дата: ${fmtDateTimeRange(normalized.startDateTime, normalized.endDateTime)}` : "",
      normalized.priceAtTime != null ? `Стоимость: ${normalized.priceAtTime} ₽` : "",
    ].filter(Boolean);
    const base = parts.length ? `${parts.join(". ")}.` : "";
    const desc = takeSnippet(normalized.description || "", 220);
    const out = safeText(`${base} ${desc}`);
    return out || "Подробная информация о групповой сессии: темы, метод и запись.";
  }, [normalized]);

  const ogImage = useMemo(() => {
    const u = normalized?.coverImg ? resolveUrl(String(normalized.coverImg)) : "";
    return u || "";
  }, [normalized?.coverImg]);

  const schemas = useMemo(() => {
    if (!normalized || !canonicalUrl) return [];

    const orgName = siteName;
    const start = normalized.startDateTime ? new Date(normalized.startDateTime).toISOString() : undefined;
    const end = normalized.endDateTime ? new Date(normalized.endDateTime).toISOString() : undefined;
    const price = Number(normalized.priceAtTime);

    const event = {
      "@context": "https://schema.org",
      "@type": "Event",
      name: normalized.title,
      description: safeText(normalized.description || ""),
      url: canonicalUrl,
      startDate: start,
      endDate: end,
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: normalized.telemostUrl ? "https://schema.org/OnlineEventAttendanceMode" : undefined,
      location: normalized.telemostUrl
        ? { "@type": "VirtualLocation", url: normalized.telemostUrl }
        : undefined,
      organizer: { "@type": "Organization", name: orgName, url: siteOrigin() || undefined },
      performer: normalized.psyName ? { "@type": "Person", name: normalized.psyName, jobTitle: "Психолог" } : undefined,
      image: ogImage || undefined,
      offers: Number.isFinite(price)
        ? {
            "@type": "Offer",
            price: String(price),
            priceCurrency: "RUB",
            url: canonicalUrl,
            availability: "https://schema.org/InStock",
          }
        : undefined,
    };

    const page = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: metaTitle,
      url: canonicalUrl,
      inLanguage: "ru-RU",
      mainEntity: event,
    };

    return [page, event];
  }, [normalized, canonicalUrl, siteName, metaTitle, ogImage]);

  const onPay = async (sessionObj, payOptions = {}) => {
    if (isBookingTemporarilyDisabled()) {
      triggerMaintenanceBanner(toast);
      return;
    }

    const sessionId = sessionObj?.id ?? id;
    const applyCode = Boolean(payOptions?.applyCode);
    const discountCode = String(payOptions?.discountCode || "").trim();
    const discountType = String(payOptions?.discountType || "PROMO").toUpperCase() === "CERTIFICATE"
      ? "CERTIFICATE"
      : "PROMO";

    if (!sessionId) return;
    if (applyCode && !discountCode) {
      toast.info("Введите код сертификата или промокода");
      return;
    }

    const telemostUrl = pickTelemostUrl(sessionObj);
    const isPaidSession = isPaidStatus(sessionObj?.status) || Boolean(sessionObj?.isPaid);

    // ✅ если сессия оплачена — показываем только вход во встречу
    if (isPaidSession) {
      if (telemostUrl) {
        if (!openExternalUrl(telemostUrl)) toast.error("Некорректная ссылка на встречу");
        return;
      }
      toast.info("Ссылка на встречу появится ближе к началу");
      return;
    }

    // ✅ если есть telemost — откроем
    const alreadyJoined = Boolean(sessionObj?.alreadyJoined) || Boolean(sessionObj?.joinedBookingId);
    if (alreadyJoined && telemostUrl) {
      if (!openExternalUrl(telemostUrl)) toast.error("Некорректная ссылка на встречу");
      return;
    }

    // ✅ если есть paymentUrl — просто уходим
    if (sessionObj?.paymentUrl && !applyCode) {
      if (!assignExternalUrl(sessionObj.paymentUrl)) toast.error("Некорректная ссылка оплаты");
      return;
    }

    if (!isAuthed) {
      setAuthRequiredOpen(true);
      return;
    }

    try {
      setPayLoading(true);

      // 1) join (если уже вступил — ок)
      let joinRes = null;
      try {
        if (typeof sessionsApi?.joinGroupSession === "function") {
          joinRes = await sessionsApi.joinGroupSession(sessionId);
        }
      } catch {
        // ignore
      }

      const joinedBookingId = joinRes?.bookingId || joinRes?.id || joinRes?.booking?.id || null;
      appointmentsApi.rememberLocalGroupJoin(sessionId, joinedBookingId, {
        title: sessionObj?.title || sessionObj?.name || null,
        coverUrl: sessionObj?.coverImg || sessionObj?.coverUrl || sessionObj?.image || sessionObj?.imageUrl || null,
        status: "PENDING_PAYMENT",
        expiresAt: Date.now() + 15 * 60 * 1000,
      });

      // 2) start payment
      let pay = null;
      if (typeof sessionsApi?.startGroupPayment === "function") {
        pay = await sessionsApi.startGroupPayment(sessionId, {
          ...(applyCode
            ? (discountType === "CERTIFICATE"
              ? { certificateCode: discountCode }
              : { promoCode: discountCode })
            : {}),
        });
      }

      const confirmationUrl = pay?.confirmationUrl || pay?.confirmation_url || pay?.url || pay?.payUrl || null;
      if (confirmationUrl) {
        if (applyCode) toast.success("Код применён, переходим к оплате…");
        if (!assignExternalUrl(confirmationUrl)) toast.error("Некорректная ссылка оплаты");
        return;
      }

      if (applyCode) {
        toast.success("Код успешно применён");
        return;
      }

      toast.success("Вы записались ✅");
    } catch (e) {
      toast.error(e?.message || "Не удалось записаться / оплатить");
    } finally {
      setPayLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="psychologists-page">
        <Helmet>
          <title>Загрузка…</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <BreadcrumbsSchema items={crumbs} />
        <div className="gs-public-loading">
          <div className="gs-public-loading__box">Загрузка групповой сессии…</div>
        </div>
      </div>
    );
  }

  if (notFound || !session) {
    return (
      <div className="psychologists-page">
        <Helmet>
          <title>Сессия не найдена</title>
          <meta name="robots" content="noindex, nofollow" />
          {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
        </Helmet>
        <BreadcrumbsSchema items={crumbs} />
        <div className="gs-public-not-found">
          <div className="gs-public-not-found__box">
            <h1>Сессия не найдена</h1>
            <p>Не удалось загрузить детали этой групповой сессии.</p>
            {error ? <p>{error}</p> : null}
            <Link to="/sessions">← К групповым сессиям</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="psychologists-page">
      <Helmet prioritizeSeoTags>
        <title>{metaTitle}</title>
        {metaDescription ? <meta name="description" content={metaDescription} /> : null}
        {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
        <meta name="robots" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:type" content="event" />
        <meta property="og:title" content={metaTitle} />
        {metaDescription ? <meta property="og:description" content={metaDescription} /> : null}
        {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
        <meta property="og:locale" content="ru_RU" />
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        {ogImage ? <meta property="og:image:alt" content={normalized?.title || metaTitle} /> : null}

        {/* Twitter */}
        <meta name="twitter:card" content={ogImage ? "summary_large_image" : "summary"} />
        <meta name="twitter:title" content={metaTitle} />
        {metaDescription ? <meta name="twitter:description" content={metaDescription} /> : null}
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}

        {/* JSON-LD */}
        {schemas.map((obj, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
        ))}
      </Helmet>

      <BreadcrumbsSchema items={crumbs} />

      <GroupSessionDetailsView session={session} onPay={onPay} payLoading={payLoading} isAuthed={isAuthed} />

      <AuthRequiredModal
        open={authRequiredOpen}
        onClose={() => setAuthRequiredOpen(false)}
        onLogin={() => {
          setAuthRequiredOpen(false);
          navigate("/login");
        }}
        title="Запись доступна после авторизации"
        message="Записаться на групповую сессию может только авторизованный пользователь."
        loginLabel="Перейти ко входу"
      />
    </div>
  );
}
